import {
  ApartmentOutlined,
  PlusOutlined,
  TeamOutlined,
  UserOutlined
} from "@ant-design/icons"
import { Button, Card, Empty, Table, Tag } from "antd"
import type { ColumnsType } from "antd/es/table"
import React, { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"

import { useAppDispatch } from "../../../hooks/use-app-dispatch.ts"
import { useAppSelector } from "../../../hooks/use-app-selector"
import { RoutesPaths } from "../../../constants/routes.ts"
import { AddBranchModal } from "../components/AddBranchModal.tsx"
import { BranchSetupReminder } from "../components/BranchSetupReminder.tsx"
import { BranchSetupWizard } from "../components/BranchSetupWizard.tsx"
import { fetchAccessibleBranches, fetchBranches } from "../redux/branches.slice.ts"
import type { Branch } from "../types/types.ts"

export default function SettingsBranchesPage() {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const branches = useAppSelector((state) => state.branches.managedBranches)
  const fetching = useAppSelector((state) => state.branches.fetchingManaged)
  const gcc = useAppSelector((state) => state.branches.gcc)
  const [addOpen, setAddOpen] = useState(false)
  const [wizardOpen, setWizardOpen] = useState(false)

  useEffect(() => {
    dispatch(fetchBranches({ include_archived: true }))
    dispatch(fetchAccessibleBranches())
  }, [dispatch])

  const columns: ColumnsType<Branch> = [
    {
      title: "Branch name",
      dataIndex: "name",
      render: (name: string, record) => (
        <a onClick={() => navigate(RoutesPaths.settings.branch(record.id))}>
          {name}
          {record.is_default_branch && (
            <Tag className="ml-2" color="blue">
              Default
            </Tag>
          )}
        </a>
      )
    },
    {
      title: "Location",
      dataIndex: "location",
      render: (location: string | null) => location || "—"
    },
    {
      title: "Manager",
      dataIndex: "manager",
      render: (manager: Branch["manager"]) =>
        manager ? (
          <span>
            <UserOutlined className="mr-1" />
            {manager.first_name} {manager.last_name}
          </span>
        ) : (
          "—"
        )
    },
    {
      title: "Team",
      dataIndex: "team_count",
      render: (count: number) => (
        <span>
          <TeamOutlined className="mr-1" />
          {count}
        </span>
      )
    },
    {
      title: "Status",
      dataIndex: "is_active",
      render: (isActive: boolean) =>
        isActive ? (
          <Tag color="green">Active</Tag>
        ) : (
          <Tag color="default">Archived</Tag>
        )
    }
  ]

  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-4">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <ApartmentOutlined /> Branches
          </h2>
          <p className="text-gray-500 text-sm">
            {gcc?.name || "Your company"} — manage the branches your team
            operates in.
          </p>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setAddOpen(true)}
          data-testid="add-branch-button"
        >
          Add branch
        </Button>
      </div>

      <BranchSetupReminder onContinueSetup={() => setWizardOpen(true)} />

      <Card>
        <Table
          rowKey="id"
          loading={fetching}
          columns={columns}
          dataSource={branches}
          pagination={false}
          locale={{
            emptyText: (
              <Empty description="No branches yet. Add your first branch." />
            )
          }}
        />
      </Card>

      <AddBranchModal open={addOpen} onClose={() => setAddOpen(false)} />
      <BranchSetupWizard open={wizardOpen} onClose={() => setWizardOpen(false)} />
    </div>
  )
}
